import { useMemo, useState } from 'react';
import { ClientEventRow } from '@entities/clients';
import { buildEventClientsFilterItems } from '../lib/buildFilterItems';
import { selectClientTableView } from '../lib/selectClientTableView';
import { mapServerSortsToOrderBy, splitActiveSorts } from '../lib/splitActiveSorts';
import {
  ActiveSort,
  CallsSortDirection,
  ClientSideFilters,
  INITIAL_CLIENT_FILTERS,
  ManagerFilterPayload,
} from '../lib/types';

export const useClientTableView = (
  baseRows: ClientEventRow[],
  onServerSortChange: (order: string[] | null) => void,
  filterSourceRows: ClientEventRow[] = baseRows
) => {
  const [clientFilters, setClientFilters] = useState<ClientSideFilters>(INITIAL_CLIENT_FILTERS);
  const [callsSortDirection, setCallsSortDirection] = useState<CallsSortDirection>(null);

  const filterItems = useMemo(() => buildEventClientsFilterItems(filterSourceRows), [filterSourceRows]);

  const view = useMemo(
    () => selectClientTableView(baseRows, clientFilters, callsSortDirection),
    [baseRows, clientFilters, callsSortDirection]
  );

  const handleSortChange = (_order: string | null, _column: string, activeSorts?: ActiveSort[]) => {
    const { callsSortDirection: nextCallsSortDirection, serverSorts } = splitActiveSorts(activeSorts);

    setCallsSortDirection(nextCallsSortDirection);

    const orderByValues = mapServerSortsToOrderBy(serverSorts);

    onServerSortChange(orderByValues.length ? orderByValues : null);
  };

  const applyBrokersFilter = (values: string[]) => {
    setClientFilters((prevFilters) => ({ ...prevFilters, selectedBrokersFilter: values }));
  };

  const applyManagerFilter = ({ isInvitingOnly, allManagers, invitingNames }: ManagerFilterPayload) => {
    setClientFilters((prevFilters) => ({
      ...prevFilters,
      isInvitingManagersFilter: isInvitingOnly,
      selectedAllManagersFilter: allManagers,
      selectedInvitingManagersFilter: invitingNames,
    }));
  };

  const selectCallsFilter = (value: string) => {
    setClientFilters((prevFilters) => ({ ...prevFilters, selectedCallsFilter: value }));
  };

  return {
    ...view,
    filterItems,
    clientFilters,
    callsSortDirection,
    applyBrokersFilter,
    applyManagerFilter,
    selectCallsFilter,
    handleSortChange,
  };
};
